import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { useFadeIn } from '../components/useFadeIn';
import MaxSong from '../Images/MaxSong.jpg';
import LiuYing from '../Images/LiuYing.jpg';
import Jason from '../Images/Jason.png';
import LiuYufan from '../Images/LiuYufan.jpg';
import AboveFooter from '../Images/abovefooter.jpg';
import AboutHero from '../Images/abouthero.jpg';
import Mission from '../Images/mission.jpg';

function FadeSection({ children, className = '' }) {
  const ref = useFadeIn();
  return <div ref={ref} className={`section-fade ${className}`}>{children}</div>;
}

const clarity = [
  { bold: 'Delivering real-time emissions intelligence', rest: "Because yesterday's data won't solve today's challenges." },
  { bold: 'Turn "what if" into "what works."', rest: 'Predict impact, stay compliant, and make data-backed decisions with confidence.' },
  { bold: 'Automating compliance & reporting', rest: 'Ensuring businesses meet global ESG standards effortlessly.' },
  { bold: 'Unlocking financial value from carbon reductions', rest: 'With integrated carbon credit trading that turns sustainability into revenue.' },
];

const teamSummary = [
  { name: 'Max Song', role: 'Founder & CEO', photo: MaxSong },
  { name: 'Liu Ying', role: 'COO', photo: LiuYing },
  { name: 'Xiangzheng Zhang (Jason)', role: 'CPO', photo: Jason },
  { name: 'Liu Yufan (Ivy)', role: 'CFO', photo: LiuYufan },
];

export default function About() {
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <main>
      {/* Hero */}
      <section className="relative h-screen flex items-end px-4 sm:px-6 pb-12 sm:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: heroVisible ? 1 : 0 }}
        >
          <img
            src={AboutHero}
            alt="About Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        </div>

        <div
          className="relative z-10 max-w-7xl mx-auto w-full transition-all duration-1000 ease-in-out"
          style={{
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 sm:mb-8">
            Where Sustainability Meets Strategy
          </h1>
          <Link to="#mission"
            className="inline-flex items-center gap-2 border border-white text-white font-body px-5 sm:px-6 py-2.5 sm:py-3 rounded-full hover:bg-white hover:text-[#2d3828] transition-all text-xs sm:text-sm">
            Learn About us <ArrowDown size={14} />
          </Link>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-[#f4f7f0] py-14 sm:py-20 px-4 sm:px-6" id="mission">
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <p className="font-body text-[#1e3019] text-base sm:text-lg md:text-2xl leading-relaxed max-w-3xl">
              The race to net zero is about efficiency, cost, and impact, but outdated emissions data keeps industries reactive. Carbonetics delivers real-time insights, making sustainability a competitive advantage.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* Mission + Clarity */}
      <section className="bg-[#f4f7f0] py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-16 items-start">
          <FadeSection>
            <div className="rounded-2xl overflow-hidden">
              <img
                src={Mission}
                alt="Our Mission"
                className="w-full object-cover"
              />
            </div>
          </FadeSection>
          <FadeSection>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1e3019] mb-3 sm:mb-4">
              Our Mission: Decarbonize. Optimize. Thrive.
            </h2>
            <p className="font-body text-[#1e3019]/60 text-xs sm:text-sm leading-relaxed mb-8 sm:mb-12">
              We're not just tracking emissions, we're rewriting the future of industrial sustainability. Our mission is to empower industries with the tools to see beyond the data, act with precision, and lead the transition to a high-performance, low-carbon economy.
            </p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1e3019] mb-3 sm:mb-4">
              Turning Complexity into Clarity
            </h2>
            <p className="font-body text-[#1e3019]/60 text-xs sm:text-sm leading-relaxed mb-6 sm:mb-8">
              Carbonetics doesn't just track emissions; it deciphers, optimizes, and monetizes them. We bridge the gap between raw data and real impact by:
            </p>
            <div className="flex flex-col gap-0">
              {clarity.map((item, i) => (
                <div key={i} className="py-3 sm:py-4 border-b border-[#1e3019]/10">
                  <span className="font-body font-semibold text-[#1e3019] text-xs sm:text-sm">{item.bold} </span>
                  <span className="font-body text-[#1e3019]/50 text-xs sm:text-sm">{item.rest}</span>
                </div>
              ))}
            </div>
          </FadeSection>
        </div>
      </section>

      {/* Team Preview */}
      <section className="bg-[#2d3828] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Our Team</h2>
            <p className="font-body text-white/60 text-sm sm:text-base max-w-lg mb-10 sm:mb-16">
              Our collective expertise and passion for innovation drive our mission to deliver impactful and environmentally-conscious solutions.
            </p>
          </FadeSection>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {teamSummary.map((member, i) => (
              <FadeSection key={i}>
                <div className="bg-[#364230] rounded-xl overflow-hidden">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-full object-contain bg-white"
                  />
                  <div className="p-3 sm:p-4">
                    <p className="font-body font-semibold text-white text-xs sm:text-sm">{member.name}</p>
                    <p className="font-body text-white/50 text-xs">{member.role}</p>
                  </div>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#4cd964] py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-lg">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#1e3019] mb-4 sm:mb-6">
              Take Action for a Greener Future
            </h2>
            <p className="font-body text-[#1e3019]/70 text-sm sm:text-base mb-8 sm:mb-12">
              Carbonetics helps businesses stay ahead with AI-driven sustainability solutions. Reduce emissions, optimize operations, and drive profitability with data-backed insights.
            </p>
            <Link to="/contact"
              className="inline-flex items-center gap-2 border border-[#1e3019] text-[#1e3019] font-body px-5 sm:px-6 py-2.5 sm:py-3 rounded-full hover:bg-[#1e3019] hover:text-white transition-all text-xs sm:text-sm">
              Get Started <ArrowRight size={14} />
            </Link>
          </div>
          <div className="shrink-0">
            <img
              src={AboveFooter}
              alt="Greener Future"
              className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-full object-cover"
            />
          </div>
        </div>
      </section>
    </main>
  );
}